import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, Calculator, ShieldCheck } from "lucide-react";
import { waLink } from "@/lib/config";

const HERO_IMG =
  "https://images.unsplash.com/photo-1574757987642-5755f0839101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjB3b3JrZXJ8ZW58MHx8fHwxNzg1NDA2MTQ5fDA&ixlib=rb-4.1.0&q=85&w=1400";

const HERO_BG =
  "https://images.unsplash.com/photo-1759383984112-0aa685194087?q=85&w=2000&auto=format&fit=crop";

const HEADLINE = ["THE FOUNDATION", "OF INDIA'S", "MEGASTRUCTURES"];

function Stat({ value, suffix, label, testid, decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <div ref={ref} data-testid={testid} className="border-l-2 border-brand/60 pl-4">
      <p className="font-mono text-2xl sm:text-3xl font-bold text-white tracking-tight">
        {display.toFixed(decimals)}
        <span className="text-brand">{suffix}</span>
      </p>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-500 mt-1">{label}</p>
    </div>
  );
}

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden grain bg-ink pt-16">
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-[118%] object-cover opacity-[0.22]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/80 to-ink" />
      </motion.div>
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-6 flex items-center gap-3"
            >
              <span className="h-px w-10 bg-brand inline-block" />
              Pan-India B2B Cement Supply & Site Intelligence
            </motion.p>

            <h1
              data-testid="hero-main-heading"
              className="font-heading font-black uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-white"
            >
              {HEADLINE.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 text-base sm:text-lg leading-relaxed text-gray-300 max-w-xl"
            >
              Every reputed cement brand, dispatched factory-direct to EPC builders, infra
              developers and contractors — with live site inventory tracking built in.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <a
                href={waLink("RFQ: I need bulk cement dispatch. Brand preference: ____ | Quantity: ____ bags | Site city: ____")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-bulk-rfq"
                className="group inline-flex items-center gap-3 bg-brand hover:bg-brand-dark text-ink font-heading font-bold uppercase tracking-wide px-7 py-4 transition-colors"
              >
                Request Bulk Dispatch
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/calculators"
                data-testid="hero-cta-open-calculators"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-brand/60 text-white hover:text-brand font-heading font-bold uppercase tracking-wide px-7 py-4 transition-colors"
              >
                <Calculator className="h-4 w-4" />
                Contractor Tools Suite
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.95 }}
              className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6"
            >
              <Stat value={9} suffix="+" label="Reputed Brands" testid="hero-stat-brands" />
              <Stat value={48} suffix="hr" label="Pan-India Dispatch" testid="hero-stat-dispatch" />
              <Stat value={2.4} suffix="M+" label="Tonnes Supplied" testid="hero-stat-volume" decimals={1} />
              <Stat value={1200} suffix="+" label="Active Sites" testid="hero-stat-contractors" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-brand" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-brand" />
            <div className="relative overflow-hidden border border-white/10 aspect-[4/5]">
              <motion.img
                src={HERO_IMG}
                alt="Concrete pour at a construction site"
                style={{ y: imgY }}
                className="absolute inset-0 w-full h-[125%] object-cover"
              />
              <div className="absolute inset-0 bg-black/35" />
              <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-ink/95 to-transparent">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5" /> Batch-tested · IS 12269 verified
                </p>
                <p className="font-heading font-bold text-white uppercase mt-1">Live pour — Metro Pier 40, Nagpur</p>
              </div>
            </div>
            <div className="absolute top-6 -right-3 bg-elevated border border-white/10 px-4 py-3 shadow-xl">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Next dispatch</p>
              <p className="font-mono text-sm font-bold text-brand flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand animate-ticker inline-block" />
                06:00 IST · 3 Rakes
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
